import * as React from 'react';

import { cn } from '../../utils/cn';
import { MarkdownContent } from './MarkdownContent';

export type MessageRole = 'user' | 'assistant' | 'system';

export interface MessageSource {
  id: string;
  label: string;
  onSelect: () => void;
}

export interface MessageBubbleProps {
  role: MessageRole;
  author?: string;
  timestamp?: string;
  content: string | React.ReactNode;
  sources?: MessageSource[];
  footer?: React.ReactNode;
  className?: string;
}

// Pastel, light, transparent feel
const roleStyles: Record<MessageRole, string> = {
  user: 'bg-blue-50/80 text-blue-900 border-blue-100',
  assistant: 'bg-neutral-50/80 text-neutral-800 border-neutral-100',
  system: 'bg-amber-50/60 text-amber-900 border-amber-100',
};

const formatTimestamp = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  role,
  author,
  timestamp,
  content,
  sources,
  footer,
  className,
}) => {
  const [sourcesExpanded, setSourcesExpanded] = React.useState(false);
  const label = author ?? (role === 'user' ? 'You' : role === 'assistant' ? 'Assistant' : 'System');
  const formattedTimestamp = formatTimestamp(timestamp);
  const isUser = role === 'user';

  return (
    <div className={cn('flex w-full flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      {/* Minimal header: just name and time */}
      <div className="flex items-center gap-2 px-1 text-[11px] text-neutral-400">
        <span className="font-medium">{label}</span>
        {formattedTimestamp && <span>· {formattedTimestamp}</span>}
      </div>

      {/* Message bubble */}
      <div
        className={cn(
          'max-w-[85%] rounded-lg border px-3 py-2 text-sm',
          roleStyles[role],
          className,
        )}
      >
        <div>{typeof content === 'string' ? <MarkdownContent content={content} /> : content}</div>

        {/* Citation buttons - subtle inline style */}
        {sources && sources.length > 0 && (
          <div className="mt-1.5">
            {!sourcesExpanded ? (
              <button
                type="button"
                onClick={() => setSourcesExpanded(true)}
                className="text-[10px] text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                {sources.length === 1 ? '· 1 source' : `· ${sources.length} sources`}
              </button>
            ) : (
              <div className="mt-1 pt-1.5 border-t border-neutral-200/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[9px] uppercase tracking-wider text-neutral-400">
                    Sources
                  </span>
                  <button
                    type="button"
                    onClick={() => setSourcesExpanded(false)}
                    className="text-[9px] text-neutral-400 hover:text-neutral-600"
                  >
                    hide
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {sources.map((source) => (
                    <button
                      key={source.id}
                      type="button"
                      onClick={source.onSelect}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 transition-colors truncate max-w-[140px]"
                      title={source.label}
                    >
                      {source.label.length > 20 ? source.label.slice(0, 20) + '…' : source.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {footer && <div className="mt-2 text-xs text-neutral-500">{footer}</div>}
      </div>
    </div>
  );
};

export default MessageBubble;
